/**
 * PlanPage — vista principal del plan activo (Sprint 6 Bloque 4).
 *
 * Orquesta:
 * 1. Fetch GET /training-plans/active al montar.
 * 2. Empty state con CTA "Crear plan" si null.
 * 3. RecommendationsCard arriba si hay recomendaciones.
 * 4. WeekNavigator + WeekView de la semana seleccionada (default = actual).
 * 5. Botón "Eliminar plan" al pie con modal de confirmación.
 *
 * Decisiones UI:
 * - Vista semanal por defecto (decisión técnica 1 Bloque 4) — se muestra
 *   la semana actual del plan al cargar; el user navega entre semanas
 *   con el WeekNavigator.
 * - Si el plan está fuera de window (carrera pasada o pre-arranque),
 *   se muestra la primera semana como fallback con un aviso.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { RecommendationsCard } from "@/components/training/RecommendationsCard";
import { WeekNavigator } from "@/components/training/WeekNavigator";
import { WeekView } from "@/components/training/WeekView";
import { getOnboardingStatus } from "@/api/onboarding";
import { getRaces } from "@/api/races";
import {
  createMaintenancePlan,
  deleteActivePlan,
  dismissRaceFinishedMessage,
  getActivePlan,
} from "@/api/training";
import { t } from "@/i18n/es";
import { evaluatePlanGate } from "@/lib/planGate";
import { PreVamGateView } from "@/pages/Plan/components/PreVamGateView";
import { PostRaceImmediateBanner } from "@/pages/Plan/components/PostRaceImmediateBanner";
import { RaceDayCard } from "@/pages/Plan/components/RaceDayCard";
import { RaceWeekSection } from "@/pages/Plan/components/RaceWeekSection";
import { VamRescheduleBanner } from "@/pages/Plan/components/VamRescheduleBanner";
import { AddRaceModal } from "@/pages/Plan/components/AddRaceModal";
import { PostRecoveryOptions } from "@/pages/Plan/components/PostRecoveryOptions";
import { ProgressionDistanceChoice } from "@/pages/Plan/components/ProgressionDistanceChoice";
import type {
  ActivePlanResult,
  OnboardingStatus,
  PlanType,
  RaceTarget,
  TrainingPlan,
} from "@/types/api";

interface Toast {
  id: number;
  message: string;
  kind: "success" | "error";
}

let toastSeq = 0;

/** Sprint 13 S13.6a (motor v10 §1/§2): etiqueta del badge de tipo de plan.
 *  El render completo de la recovery week llega en S13.6b; aquí el badge
 *  básico evita que aparezca como un plan normal sin identificar. */
function _planTypeBadgeLabel(planType: PlanType): string {
  switch (planType) {
    case "progression":
      return t.plan.planTypeBadgeProgression;
    case "recovery":
      return t.plan.planTypeBadgeRecovery;
    case "race":
    default:
      return t.plan.planTypeBadgeRace;
  }
}

/** Devuelve el index 0-based de la semana REGULAR que contiene `today`,
 *  o null si hoy está fuera del rango del plan o cae dentro de la
 *  race_week (gap-fill v16.3 §G.1). Las weeks regulares son las que
 *  navegamos con WeekNavigator/WeekView; la race_week se renderiza como
 *  sección dedicada fuera de la navegación. */
function _todayWeekIndex(plan: TrainingPlan): number | null {
  // s63 (S-3 #5): "hoy" en fecha LOCAL del dispositivo — el código
  // anterior usaba toISOString() (UTC), que entre las 00:00 y las
  // ~02:00 hora española devolvía el día ANTERIOR y podía marcar la
  // semana equivocada en la frontera domingo→lunes. Criterio de
  // frontera documentado: semana = [start, start+7d) comparando
  // fechas-calendario locales (los start_date del plan son fechas sin
  // hora; el usuario vive en su zona horaria, no en UTC).
  const todayIso = _localIso(new Date());
  const regularWeeks = plan.weeks.filter((w) => w.phase !== "race_week");
  for (let i = 0; i < regularWeeks.length; i++) {
    const start = regularWeeks[i].start_date;
    const endIso = _addDaysIso(start, 7);
    if (todayIso >= start && todayIso < endIso) return i;
  }
  return null;
}

/** YYYY-MM-DD de una fecha en la zona horaria LOCAL del dispositivo. */
function _localIso(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** ISO + n días, en aritmética de calendario local (inmune a DST). */
function _addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return _localIso(new Date(y, m - 1, d + days));
}

export function PlanPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ActivePlanResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Sprint 13 S13.10b: modal "añadir carrera" (solo plan de progresión).
  const [showAddRace, setShowAddRace] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Sprint 11 S11.2: onboarding necesario para evaluar el gate pre-VAM.
  // Fail-safe: si el fetch falla, queda null y el gate evalúa inactive
  // (mostrar plan completo es más seguro que bloquearlo por error de
  // red).
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null);
  // v16.3 B.3: lista de race_targets para resolver el primary que ancla
  // el plan activo race. Fetch paralelo al refresh principal (mismo patrón
  // que onboarding). Null = no resolvido todavía o sin races; el caller
  // de RaceDayCard mostrará solo info (no botón) si no hay primary.
  const [races, setRaces] = useState<RaceTarget[] | null>(null);
  // v16.5.X T1 4b sub-paquete X: optimistic dismiss del PostRaceImmediateBanner.
  // El backend re-evalúa el flag tras 24h del dismiss (snooze). Este state
  // local lo oculta INSTANT (sin esperar al refresh) tras click X.
  const [raceFinishedDismissedLocal, setRaceFinishedDismissedLocal] =
    useState(false);

  const showToast = useCallback(
    (message: string, kind: "success" | "error" = "success") => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((tt) => tt.id !== id));
      }, 5000);
    },
    [],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getActivePlan();
      setData(result);
      if (result.plan) {
        const todayIdx = _todayWeekIndex(result.plan);
        // Default: si hoy está en el plan, abrir esa semana. Si no, la 0.
        setSelectedWeekIndex(todayIdx ?? 0);
      }
    } catch {
      setError(t.plan.toastError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // v16.3 B.3: fetch lazy de race_targets para resolver el primary que
  // ancla el plan activo race. Independiente del refresh principal. Si
  // falla, races queda null → RaceDayCard mostrará info sin botón
  // "Reportar resultado" (defensa: atleta puede ir a /carreras).
  const refreshRaces = useCallback(async () => {
    try {
      const result = await getRaces();
      setRaces(result.races);
    } catch {
      setRaces(null);
    }
  }, []);

  useEffect(() => {
    void refreshRaces();
  }, [refreshRaces]);

  // Sprint 11 S11.2: fetch lazy del onboarding status. Independiente
  // del refresh principal del plan (paralelizamos). Fail-safe: error
  // → onboarding queda null → gate evalúa inactive.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getOnboardingStatus();
        if (!cancelled) setOnboarding(status);
      } catch {
        if (!cancelled) setOnboarding(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const todayWeekIndex = useMemo(
    () => (data?.plan ? _todayWeekIndex(data.plan) : null),
    [data?.plan],
  );

  // v16.5.X T1 4b sub-paquete X: handlers del PostRaceImmediateBanner.
  // Dismiss: optimistic local + POST backend. Si POST falla, dejamos el
  // banner oculto en esta sesión (el atleta tendrá otra oportunidad en
  // el próximo refresh — el flag re-aparece tras 24h del último dismiss
  // que SÍ persistiera).
  const handleRaceFinishedDismiss = useCallback(async () => {
    setRaceFinishedDismissedLocal(true);
    try {
      await dismissRaceFinishedMessage();
    } catch {
      // Silenciamos errores: el optimistic update local ya ocultó el
      // banner; un refresh posterior puede re-mostrarlo si el POST no
      // persistió, lo cual es comportamiento aceptable.
    }
  }, []);

  // Sub-paquete Y v16.5.AB Y.C.1: el click en "Mantenimiento" del banner
  // POST a /training-plans plan_type='maintenance' (Y.B.4 backend) y luego:
  // 1. Muestra toast contextual según `was_reset` / `cycle_number`.
  // 2. Oculta el banner localmente (optimistic — el backend ya soft-deleteó
  //    la recovery week dentro de la transacción del persister).
  // 3. Refresca el plan activo para que el atleta vea el maintenance recién
  //    creado en lugar del recovery anterior.
  const handleRaceFinishedMantenimiento = useCallback(async () => {
    try {
      const response = await createMaintenancePlan();
      let toastMessage: string;
      if (response.was_reset) {
        toastMessage = t.plan.maintenanceToastReset;
      } else if (response.cycle_number === 0) {
        toastMessage = t.plan.maintenanceToastFirst;
      } else {
        // cycle_number 1..5 → mostrar como ciclo 2..6 (1-based al usuario).
        toastMessage = t.plan.maintenanceToastContinuation.replace(
          "{cycle}",
          (response.cycle_number + 1).toString(),
        );
      }
      showToast(toastMessage, "success");
      setRaceFinishedDismissedLocal(true);
      await refresh();
    } catch (error) {
      console.error("[Y.C.1] createMaintenancePlan failed", error);
      showToast(t.plan.maintenanceToastError, "error");
    }
  }, [refresh, showToast]);

  async function onConfirmDelete() {
    setDeleting(true);
    try {
      await deleteActivePlan();
      showToast(t.plan.toastPlanDeleted, "success");
      setData(null);
      setShowDeleteModal(false);
    } catch {
      showToast(t.plan.toastError, "error");
    } finally {
      setDeleting(false);
    }
  }

  // Toast stack reutilizable entre todos los renders. Lo renderizamos
  // como hermano fuera del <main> activo para que un cambio de estado
  // (p.ej. data → null tras delete) no desmonte los toasts en flight.
  const toastStack = toasts.length > 0 ? (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-toast right-4 z-50 flex max-w-xs flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          data-testid={`toast-${toast.kind}`}
          className="pointer-events-auto rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone"
        >
          {toast.message}
        </div>
      ))}
    </div>
  ) : null;

  if (loading) {
    return (
      <>
        <main
          data-testid="plan-loading"
          className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone"
        >
          <p className="text-xs uppercase tracking-widest text-gray-mid">
            {t.plan.pageTitle.toLowerCase()}
          </p>
          <p className="text-sm text-bone-muted">{t.plan.pageTitle}…</p>
        </main>
        {toastStack}
      </>
    );
  }

  if (error) {
    return (
      <>
        <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone">
          <p className="text-sm text-danger" data-testid="plan-error">
            {error}
          </p>
          <button
            type="button"
            onClick={() => void refresh()}
            className="self-start rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
          >
            Reintentar
          </button>
        </main>
        {toastStack}
      </>
    );
  }

  // Sprint 13 S13.10a/b: sin plan activo (`data === null` post-delete/pre-fetch,
  // o `data.plan === null` del backend). Tres sub-estados:
  //   (b) progresión vencida → pantalla elige-distancia (S13.10a).
  //   (d) recovery week terminada → pantalla 2 opciones (S13.10b).
  //   (c) resto → empty state "crear plan".
  if (data === null || data.plan === null) {
    // (b) Elige-distancia: una progresión venció y el atleta debe elegir
    //     la distancia de la siguiente (v10.1 §2). Al crear, refresca.
    if (data?.progressionDistanceChoicePending) {
      return (
        <>
          <ProgressionDistanceChoice
            onCreated={() => void refresh()}
            athleteLevel={onboarding?.profile?.level ?? null}
          />
          {toastStack}
        </>
      );
    }

    // (d) Post-recovery: una recovery week terminó → 2 opciones (preparar
    //     carrera o seguir progresando). Sprint 13 S13.10b (v10 §1).
    if (data?.postRecoveryOptionsAvailable) {
      return (
        <>
          <PostRecoveryOptions onCreated={() => void refresh()} />
          {toastStack}
        </>
      );
    }

    // (c) Empty state "crear plan" (usuario sin plan / post-delete).
    return (
      <>
        <main
          data-testid="plan-empty"
          className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone"
        >
          <header>
            <p className="text-xs uppercase tracking-widest text-gray-mid">
              {t.plan.pageTitle.toLowerCase()}
            </p>
            <h1 className="mt-6 text-2xl font-medium tracking-tight">
              {t.plan.notFoundTitle}
            </h1>
            <p className="mt-2 text-sm text-bone-muted">{t.plan.notFoundCta}</p>
          </header>
          <button
            type="button"
            onClick={() => navigate("/plan/crear")}
            className="self-start rounded-md bg-lime px-4 py-2.5 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
          >
            {t.plan.notFoundButton}
          </button>
        </main>
        {toastStack}
      </>
    );
  }

  // data.plan garantizado no-null tras el guard anterior.
  const plan = data.plan;
  const recommendations = data.recommendations;

  // v16.3 §G.2: la `race_week` (gap-fill backend §G.1) NO entra en el
  // navegador — se renderiza como sección dedicada al final. Particionamos
  // explícitamente para que selectedWeekIndex/_todayWeekIndex operen solo
  // sobre weeks regulares (paridad con `plan.total_weeks`, que también las
  // excluye). Backend invariante: la race_week, si existe, es la ÚLTIMA
  // del array y aparece a lo sumo una vez.
  const regularWeeks = plan.weeks.filter((w) => w.phase !== "race_week");
  const raceWeek = plan.weeks.find((w) => w.phase === "race_week") ?? null;
  const currentWeek = regularWeeks[selectedWeekIndex];

  // Sprint 11 S11.2 (motor v8 §1): gate "plan oculto hasta TEST-VAM
  // completado". Si activo → render PreVamGateView en lugar de
  // F3 (s51): estado de exportación FIT — espejo de los gates backend.
  // amateur + reloj: "enabled" con VAM medida, "needs_vam" sin ella.
  // Cualquier otro caso → undefined (el botón no aparece).
  const fitProfile = onboarding?.profile;
  const fitExportState: "enabled" | "needs_vam" | undefined =
    fitProfile?.level === "amateur" && fitProfile?.has_watch === true
      ? (fitProfile?.vam_m_per_min ? "enabled" : "needs_vam")
      : undefined;

  // WeekNavigator + WeekView. Pure derivation, sin side effects.
  const gateState = evaluatePlanGate(onboarding, plan);

  return (
    <>
    <main className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone">
      <header>
        <p className="text-xs uppercase tracking-widest text-gray-mid">
          {t.plan.pageTitle.toLowerCase()}
        </p>
        <h1 className="mt-6 text-2xl font-medium tracking-tight">
          {t.plan.pageTitle}
        </h1>
        {/* Sprint 13 S13.6a: etiqueta de tipo de plan. Sprint 14.0: estilo
            PLANO (no píldora/borde/fondo) para que NO parezca un botón
            pulsable — el beta tester intentó pulsarla. Es informativa. */}
        <p
          data-testid="plan-type-badge"
          className="mt-3 text-xs uppercase tracking-widest text-gray-mid"
        >
          {_planTypeBadgeLabel(plan.plan_type)}
        </p>
      </header>

      {/* Sprint 11 S11.5 (motor v8 §3): banner one-shot si backend
          acaba de reprogramar test_vam missed. Aparece arriba del
          resto para que el atleta lo vea primero. */}
      {plan.recent_vam_reschedule && (
        <VamRescheduleBanner
          oldDate={plan.recent_vam_reschedule.old_date}
          newDate={plan.recent_vam_reschedule.new_date}
        />
      )}

      {/* v16.5.X T1 4b sub-paquete X (Alberto 19/06/26): banner inmediato
          post-race con 2 botones (Empezar plan nuevo + Mantenimiento
          Próximamente). Se muestra ENCIMA de la recovery week activa
          tras la transición race→recovery; persistente con re-aparición
          24h y expiración 30d gestionadas por el backend. */}
      {data?.raceJustFinished && !raceFinishedDismissedLocal && (
        <PostRaceImmediateBanner
          onDismiss={() => void handleRaceFinishedDismiss()}
          onMaintenanceClick={handleRaceFinishedMantenimiento}
        />
      )}

      {/* Generation warning si la carrera está cerca (Regla 6 — backend
          ya lo sirvió como string en plan.generation_warning). */}
      {plan.generation_warning && (
        <section
          data-testid="generation-warning"
          className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-bone"
        >
          {plan.generation_warning}
        </section>
      )}

      {recommendations.length > 0 && (
        <RecommendationsCard recommendations={recommendations} />
      )}

      {/* Sprint 11 S11.2: bifurcación gate. Si activo → solo TEST-VAM.
          Si inactivo → render normal (navegación + week view). */}
      {gateState.isActive && gateState.testVamSession ? (
        <PreVamGateView
          session={gateState.testVamSession}
          introSession={gateState.introSession}
        />
      ) : (
        <>
          <WeekNavigator
            currentIndex={selectedWeekIndex}
            totalWeeks={plan.total_weeks}
            currentWeekNumber={currentWeek.week_number}
            phase={currentWeek.phase}
            todayWeekIndex={todayWeekIndex}
            weekStartDate={currentWeek.start_date}
            onChange={setSelectedWeekIndex}
          />
          <WeekView
            week={currentWeek}
            hasWatch={onboarding?.profile?.has_watch === true}
            fitExport={fitExportState}
            onSessionUpdated={() => void refresh()}
            flexibleScheduling={
              onboarding?.profile?.flexible_scheduling === true
            }
          />
        </>
      )}

      {/* v16.3 §G.2 + v16.4 §1: race_week (gap-fill). Solo si backend
          la inyectó (gap >= 1). Se renderiza fija debajo del navegador
          de weeks regulares para que el atleta vea siempre los últimos
          días pre-carrera sin tener que navegar. */}
      {raceWeek && (
        <RaceWeekSection
          week={raceWeek}
          hasWatch={onboarding?.profile?.has_watch === true}
            fitExport={fitExportState}
            onSessionUpdated={() => void refresh()}
        />
      )}

      {/* v16.3 §1.1 B.3: tarjeta del día de la carrera. Solo en planes
          'race' (no progression/recovery). primaryRace puede ser null si
          getRaces falló o atleta sin races — RaceDayCard mostrará solo
          info sin botón "Reportar resultado" en ese caso. */}
      {plan.plan_type === "race" && (
        <RaceDayCard
          plan={plan}
          primaryRace={races?.find((r) => r.is_primary) ?? null}
          onResultSaved={() => {
            showToast(t.plan.raceDayCardResultSavedToast, "success");
            void refresh();
            void refreshRaces();
          }}
        />
      )}

      {/* Sprint 13 S13.10b: "Añadir carrera" — solo en Plan de Progresión.
          Reorienta el plan hacia una carrera (asfalto o trail). */}
      {plan.plan_type === "progression" && (
        <button
          type="button"
          data-testid="add-race-btn"
          onClick={() => setShowAddRace(true)}
          className="self-start rounded-md border border-border-subtle bg-carbon-raised px-4 py-2 text-sm text-bone transition-colors duration-fast hover:border-border-strong"
        >
          {t.plan.addRaceButton}
        </button>
      )}

      {/* s64: en móvil "Volver" y "Mis carreras" son redundantes con la
          barra inferior (Inicio · Carreras) → ocultos < md; en desktop
          se conservan tal cual. "Eliminar plan" intacto en todas partes
          (destructivo ≠ navegación) y, solo en móvil, queda a la derecha
          como hasta ahora (justify-end). */}
      <div className="mt-auto flex items-center justify-end gap-4 md:justify-between">
        <Link
          to="/"
          data-testid="plan-back-link"
          className="hidden text-xs text-bone-muted hover:text-bone md:block"
        >
          ← Volver
        </Link>
        {/* v15.5-§8.3.C: entry point a /carreras. Link discreto entre
            "Volver" y "Borrar plan", visible siempre en desktop (no
            condicionado a plan_type). */}
        <Link
          to="/carreras"
          data-testid="races-link"
          className="hidden text-xs text-bone-muted hover:text-bone md:block"
        >
          {t.plan.racesLinkLabel}
        </Link>
        <button
          type="button"
          data-testid="delete-plan-btn"
          onClick={() => setShowDeleteModal(true)}
          className="text-xs text-bone-muted underline-offset-4 hover:text-danger hover:underline"
        >
          {t.plan.deletePlanButton}
        </button>
      </div>

      {/* Modal de confirmación de borrado */}
      {showDeleteModal && (
        <div
          data-testid="delete-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        >
          <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-5">
            <h2
              id="delete-modal-title"
              className="text-lg font-medium text-bone"
            >
              {t.plan.deleteModalTitle}
            </h2>
            <p className="mt-2 text-sm text-bone-muted">
              {t.plan.deleteModalBody}
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
              >
                {t.plan.skipModalCancel}
              </button>
              <button
                type="button"
                data-testid="delete-modal-confirm"
                onClick={() => void onConfirmDelete()}
                disabled={deleting}
                className="rounded-md bg-danger px-4 py-2 text-sm font-medium text-white transition-opacity duration-fast hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t.plan.deleteModalConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

    </main>

    {/* Sprint 13 S13.10b: modal añadir-carrera (form + confirmación trail). */}
    {showAddRace && (
      <AddRaceModal
        onClose={() => setShowAddRace(false)}
        onAdded={() => void refresh()}
        athleteLevel={onboarding?.profile?.level ?? null}
      />
    )}
    {toastStack}
    </>
  );
}
